import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import Navbar from '@/components/feature/Navbar';
import StorageImage from '@/components/base/StorageImage';

interface BlogPostData {
  id?: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  cover_image: string;
  author: string;
  status: string;
  category_id: number | null;
  tags: string[];
  seo_title: string;
  seo_description: string;
}

interface BlogCategory {
  id: number;
  name: string;
}

export default function PostEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const [post, setPost] = useState<BlogPostData>({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    cover_image: '',
    author: 'Carol L Berry, Real Estate Professional',
    status: 'draft',
    category_id: null,
    tags: [],
    seo_title: '',
    seo_description: '',
  });

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [coverUploading, setCoverUploading] = useState(false);
  const [wordUploading, setWordUploading] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Fetch post data for editing
  useEffect(() => {
    const init = async () => {
      try {
        const { data: cats } = await supabase.from('blog_categories').select('id, name').order('sort_order');
        setCategories((cats as BlogCategory[]) || []);

        if (!isNew) {
          const { data, error: fetchError } = await supabase
            .from('blog_posts')
            .select('*')
            .eq('id', Number(id))
            .maybeSingle();

          if (fetchError) throw fetchError;
          if (data) {
            setPost({
              title: data.title || '',
              slug: data.slug || '',
              excerpt: data.excerpt || '',
              content: data.content || '',
              cover_image: data.cover_image || '',
              author: data.author || 'Carol L Berry, Real Estate Professional',
              status: data.status || 'draft',
              category_id: data.category_id || null,
              tags: data.tags || [],
              seo_title: data.seo_title || '',
              seo_description: data.seo_description || '',
            });
          }
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load post');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [isNew, id]);

  const updateField = useCallback(<K extends keyof BlogPostData>(field: K, value: BlogPostData[K]) => {
    setPost((prev) => ({ ...prev, [field]: value }));
    setUnsavedChanges(true);
  }, []);

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .slice(0, 80);
  };

  const handleTitleChange = (value: string) => {
    setPost((prev) => {
      const newSlug = !prev.slug || prev.slug === generateSlug(prev.title)
        ? generateSlug(value)
        : prev.slug;
      return { ...prev, title: value, slug: newSlug };
    });
    setUnsavedChanges(true);
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCoverUploading(true);
    try {
      const fileExt = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
      const safeExt = fileExt || 'jpg';
      const fileName = `blog/cover-${Date.now()}.${safeExt}`;

      const { error: uploadError } = await supabase.storage
        .from('public')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      setPost((prev) => ({ ...prev, cover_image: fileName }));
      setUnsavedChanges(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to upload cover image');
    } finally {
      setCoverUploading(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  const handleWordUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setWordUploading(true);
    try {
      const mammoth = await import('mammoth');
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setPost((prev) => ({ ...prev, content: result.value }));
      setUnsavedChanges(true);
      setSuccess('Word document imported successfully! Review and edit before publishing.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to import Word document. Please try a .docx file.');
    } finally {
      setWordUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !post.tags.includes(tag)) {
      setPost((prev) => ({ ...prev, tags: [...prev.tags, tag] }));
      setUnsavedChanges(true);
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setPost((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
    setUnsavedChanges(true);
  };

  const savePost = async (status: string = post.status) => {
    setSaving(true);
    setError('');
    try {
      const payload: Record<string, any> = {
        ...post,
        status,
        user_id: user?.id || null,
        updated_at: new Date().toISOString(),
      };

      // Only set published_at when publishing for the first time; never send undefined
      if (status === 'published' && post.status !== 'published') {
        payload.published_at = new Date().toISOString();
      } else {
        delete payload.published_at;
      }

      // Remove id from payload for insert/update (id is handled separately)
      delete payload.id;

      if (isNew) {
        const { data, error: insertError } = await supabase
          .from('blog_posts')
          .insert(payload)
          .select('id')
          .single();

        if (insertError) {
          // Extract the most helpful error text from Supabase errors
          const errAny = insertError as any;
          const detail = errAny.details || errAny.detail || errAny.hint || errAny.code || '';
          const msg = errAny.message || 'Insert failed';
          throw new Error(detail ? `${msg} (${detail})` : msg);
        }

        setUnsavedChanges(false);
        setSuccess('Post created!');

        if (status === 'published') {
          navigate(`/admin/posts/${data.id}/edit`);
        } else {
          navigate(`/admin/posts/${data.id}/edit`, { replace: true });
        }
      } else {
        const { error: updateError } = await supabase
          .from('blog_posts')
          .update(payload)
          .eq('id', Number(id));

        if (updateError) {
          const errAny = updateError as any;
          const detail = errAny.details || errAny.detail || errAny.hint || errAny.code || '';
          const msg = errAny.message || 'Update failed';
          throw new Error(detail ? `${msg} (${detail})` : msg);
        }

        setUnsavedChanges(false);
        setSuccess(status === 'published' ? 'Post published!' : 'Post saved!');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save post';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    await savePost('draft');
    // Navigate within the same tab so the Readdy preview context is preserved
    navigate(`/blog/${post.slug}`);
  };

  const handleNavClick = (path: string) => {
    if (unsavedChanges) {
      setPendingNavigation(path);
      setShowLeaveWarning(true);
    } else {
      navigate(path);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      {/* Admin Navbar */}
      <header className="sticky top-0 z-50 bg-foreground-950">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between py-2.5">
            <div className="flex items-center gap-4">
              <span className="font-heading text-accent-100 text-sm tracking-wide">CAROL L BERRY</span>
              <span className="text-accent-300/50 text-xs">|</span>
              <span className="text-accent-300 font-body text-xs tracking-wide">
                {isNew ? 'New Post' : 'Edit Post'}
              </span>
              {unsavedChanges && (
                <span className="text-accent-400 font-body text-[10px] ml-1">(Unsaved changes)</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleNavClick('/')}
                className="text-accent-300 font-body text-xs hover:text-accent-100 transition-colors whitespace-nowrap px-2"
              >
                Home
              </button>
              <button
                onClick={() => handleNavClick('/blog')}
                className="text-accent-300 font-body text-xs hover:text-accent-100 transition-colors whitespace-nowrap px-2"
              >
                Blog
              </button>
              <button
                onClick={() => handleNavClick('/admin')}
                className="text-accent-300 font-body text-xs hover:text-accent-100 transition-colors whitespace-nowrap px-2"
              >
                <i className="ri-arrow-left-line mr-1" />All Posts
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Editor */}
      <div className="flex-1 py-8 md:py-10">
        <div className="max-w-[900px] mx-auto px-4 md:px-6 lg:px-10">
          {error && (
            <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200">
              <p className="text-red-700 font-body text-xs">{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200">
              <p className="text-green-700 font-body text-xs">{success}</p>
            </div>
          )}

          {/* Action buttons - top */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="font-heading text-foreground-900 text-xl">
              {isNew ? 'Create Blog Post' : 'Edit Blog Post'}
            </h1>
            <div className="flex items-center gap-2">
              <button
                onClick={() => savePost('draft')}
                disabled={saving}
                className="px-4 py-2 rounded-md text-sm font-body font-medium text-foreground-600 border border-background-300 hover:bg-background-100 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                onClick={handlePreview}
                disabled={saving || !post.title}
                className="px-4 py-2 rounded-md text-sm font-body font-medium text-foreground-600 border border-background-300 hover:bg-background-100 transition-colors whitespace-nowrap disabled:opacity-50 flex items-center gap-1.5"
              >
                <i className="ri-eye-line" />Preview
              </button>
              <button
                onClick={() => savePost('published')}
                disabled={saving || !post.title}
                className="px-4 py-2 rounded-md text-sm font-body font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-50 flex items-center gap-1.5"
              >
                <i className="ri-upload-line" />{isNew ? 'Publish' : post.status === 'published' ? 'Update' : 'Publish'}
              </button>
            </div>
          </div>

          {/* Title */}
          <div className="mb-4">
            <input
              type="text"
              value={post.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Post title..."
              className="w-full border-0 border-b-2 border-background-200 px-0 py-2 text-2xl md:text-3xl font-heading text-foreground-900 placeholder-foreground-300 focus:outline-none focus:border-primary-400 bg-transparent"
            />
          </div>

          {/* Slug */}
          <div className="mb-5">
            <label className="text-foreground-500 font-body text-xs mb-1 block">URL Slug</label>
            <div className="flex items-center gap-1">
              <span className="text-foreground-400 font-body text-sm">/blog/</span>
              <input
                type="text"
                value={post.slug}
                onChange={(e) => updateField('slug', e.target.value)}
                className="flex-1 border border-background-300 rounded-md px-3 py-1.5 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-background-50"
              />
            </div>
          </div>

          {/* Metadata row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            <div>
              <label className="text-foreground-500 font-body text-xs mb-1 block">Author</label>
              <input
                type="text"
                value={post.author}
                onChange={(e) => updateField('author', e.target.value)}
                className="w-full border border-background-300 rounded-md px-3 py-1.5 text-sm font-body text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-background-50"
              />
            </div>
            <div>
              <label className="text-foreground-500 font-body text-xs mb-1 block">Category</label>
              <select
                value={post.category_id ?? ''}
                onChange={(e) => updateField('category_id', e.target.value ? Number(e.target.value) : null)}
                className="w-full border border-background-300 rounded-md px-3 py-1.5 text-sm font-body text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-background-50"
              >
                <option value="">Uncategorized</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-foreground-500 font-body text-xs mb-1 block">Status</label>
              <select
                value={post.status}
                onChange={(e) => updateField('status', e.target.value)}
                className="w-full border border-background-300 rounded-md px-3 py-1.5 text-sm font-body text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-background-50"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="scheduled">Scheduled</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          {/* Tags */}
          <div className="mb-5">
            <label className="text-foreground-500 font-body text-xs mb-1 block">Tags</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {post.tags.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1 bg-primary-50 text-primary-600 font-body text-xs px-2 py-0.5 rounded-full">
                  {tag}
                  <button onClick={() => removeTag(tag)} className="hover:text-primary-800">
                    <i className="ri-close-line text-xs" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="Add a tag..."
                className="border border-background-300 rounded-md px-3 py-1.5 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-background-50 w-[180px]"
              />
              <button
                onClick={addTag}
                className="px-3 py-1.5 rounded-md text-xs font-body font-medium bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                Add
              </button>
            </div>
          </div>

          {/* Excerpt */}
          <div className="mb-5">
            <label className="text-foreground-500 font-body text-xs mb-1 block">Excerpt</label>
            <textarea
              value={post.excerpt}
              onChange={(e) => updateField('excerpt', e.target.value)}
              rows={3}
              className="w-full border border-background-300 rounded-md px-3 py-2 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-background-50 resize-none"
              placeholder="A short summary of the post..."
            />
          </div>

          {/* Cover Image */}
          <div className="mb-5">
            <label className="text-foreground-500 font-body text-xs mb-2 block">Cover Image</label>
            {post.cover_image ? (
              <div className="relative inline-block mb-3">
                <StorageImage
                  path={post.cover_image}
                  alt="Cover preview"
                  className="max-w-[400px] rounded-lg border border-background-200/70"
                />
                <button
                  onClick={() => updateField('cover_image', '')}
                  className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-foreground-950/60 text-white hover:bg-foreground-950/80 transition-colors"
                  title="Remove image"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              </div>
            ) : (
              <div className="w-full max-w-[400px] h-[180px] border-2 border-dashed border-background-300 rounded-lg flex flex-col items-center justify-center gap-2 mb-3 bg-background-100/50">
                <i className="ri-image-add-line text-foreground-400 text-2xl" />
                <p className="text-foreground-400 font-body text-xs">Upload a cover image</p>
                <p className="text-foreground-300 font-body text-[10px]">JPG, PNG, or WebP — recommended 1200×630px</p>
              </div>
            )}
            <div>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleCoverUpload}
                className="hidden"
                id="cover-upload"
              />
              <label
                htmlFor="cover-upload"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-body font-medium bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                {coverUploading ? (
                  <>
                    <div className="w-3 h-3 border-2 border-foreground-500 border-t-transparent rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="ri-upload-line text-sm" />
                    {post.cover_image ? 'Replace Image' : 'Upload Image'}
                  </>
                )}
              </label>
              {post.cover_image && (
                <div className="mt-2">
                  <input
                    type="text"
                    value={post.cover_image}
                    onChange={(e) => updateField('cover_image', e.target.value)}
                    placeholder="Or paste image URL..."
                    className="w-full border border-background-300 rounded-md px-3 py-1.5 text-xs font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-background-50"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Content - HTML Text Editor with Word Import */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <label className="text-foreground-500 font-body text-xs">Content</label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".docx,.doc"
                  onChange={handleWordUpload}
                  className="hidden"
                  id="word-upload"
                />
                <label
                  htmlFor="word-upload"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-body font-medium bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {wordUploading ? (
                    <>
                      <div className="w-3 h-3 border-2 border-accent-700 border-t-transparent rounded-full animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <i className="ri-file-word-2-line text-sm" />
                      Upload Word Document
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Rich text toolbar */}
            <div className="flex flex-wrap items-center gap-0.5 p-1.5 border border-background-300 rounded-t-md bg-background-100/50">
              {[
                { icon: 'ri-bold', action: 'bold', title: 'Bold' },
                { icon: 'ri-italic', action: 'italic', title: 'Italic' },
                { icon: 'ri-underline', action: 'underline', title: 'Underline' },
              ].map((btn) => (
                <button
                  key={btn.action}
                  type="button"
                  title={btn.title}
                  onClick={() => document.execCommand(btn.action)}
                  className="w-7 h-7 flex items-center justify-center rounded text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors text-sm"
                >
                  <i className={btn.icon} />
                </button>
              ))}
              <span className="w-px h-5 bg-background-300 mx-0.5" />
              {[
                { icon: 'ri-h-1', tag: 'h2', title: 'Heading 2' },
                { icon: 'ri-h-2', tag: 'h3', title: 'Heading 3' },
                { icon: 'ri-paragraph', tag: 'p', title: 'Paragraph' },
              ].map((btn) => (
                <button
                  key={btn.tag}
                  type="button"
                  title={btn.title}
                  onClick={() => document.execCommand('formatBlock', false, btn.tag)}
                  className="w-7 h-7 flex items-center justify-center rounded text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors text-sm"
                >
                  <i className={btn.icon} />
                </button>
              ))}
              <span className="w-px h-5 bg-background-300 mx-0.5" />
              {[
                { icon: 'ri-list-unordered', action: 'insertUnorderedList', title: 'Bulleted List' },
                { icon: 'ri-list-ordered', action: 'insertOrderedList', title: 'Numbered List' },
                { icon: 'ri-double-quotes-l', action: 'formatBlock', value: 'blockquote', title: 'Block Quote' },
              ].map((btn) => (
                <button
                  key={btn.action + (btn.value || '')}
                  type="button"
                  title={btn.title}
                  onClick={() => {
                    if (btn.value) {
                      document.execCommand(btn.action, false, btn.value);
                    } else {
                      document.execCommand(btn.action);
                    }
                  }}
                  className="w-7 h-7 flex items-center justify-center rounded text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors text-sm"
                >
                  <i className={btn.icon} />
                </button>
              ))}
              <span className="w-px h-5 bg-background-300 mx-0.5" />
              {[
                { icon: 'ri-link', action: 'createLink', title: 'Insert Link' },
                { icon: 'ri-link-unlink', action: 'unlink', title: 'Remove Link' },
              ].map((btn) => (
                <button
                  key={btn.action}
                  type="button"
                  title={btn.title}
                  onClick={() => {
                    if (btn.action === 'createLink') {
                      const url = prompt('Enter URL:');
                      if (url) document.execCommand('createLink', false, url);
                    } else {
                      document.execCommand('unlink');
                    }
                  }}
                  className="w-7 h-7 flex items-center justify-center rounded text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors text-sm"
                >
                  <i className={btn.icon} />
                </button>
              ))}
              <span className="w-px h-5 bg-background-300 mx-0.5" />
              <button
                type="button"
                title="Insert Image"
                onClick={() => {
                  const url = prompt('Enter image URL:');
                  if (url) document.execCommand('insertImage', false, url);
                }}
                className="w-7 h-7 flex items-center justify-center rounded text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors text-sm"
              >
                <i className="ri-image-line" />
              </button>
              <button
                type="button"
                title="Horizontal Divider"
                onClick={() => document.execCommand('insertHorizontalRule')}
                className="w-7 h-7 flex items-center justify-center rounded text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors text-sm"
              >
                <i className="ri-separator" />
              </button>
              <span className="w-px h-5 bg-background-300 mx-0.5" />
              <button
                type="button"
                title="Undo"
                onClick={() => document.execCommand('undo')}
                className="w-7 h-7 flex items-center justify-center rounded text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors text-sm"
              >
                <i className="ri-arrow-go-back-line" />
              </button>
              <button
                type="button"
                title="Redo"
                onClick={() => document.execCommand('redo')}
                className="w-7 h-7 flex items-center justify-center rounded text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors text-sm"
              >
                <i className="ri-arrow-go-forward-line" />
              </button>
              <button
                type="button"
                title="Clear Formatting"
                onClick={() => document.execCommand('removeFormat')}
                className="w-7 h-7 flex items-center justify-center rounded text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors text-sm"
              >
                <i className="ri-format-clear" />
              </button>
            </div>

            {/* Editor area */}
            <div
              className="border border-t-0 border-background-300 rounded-b-md min-h-[400px]"
            >
              <textarea
                value={post.content}
                onChange={(e) => updateField('content', e.target.value)}
                className="w-full h-full min-h-[400px] p-4 text-sm font-body text-foreground-900 placeholder-foreground-300 focus:outline-none bg-background-50 resize-y"
                placeholder="Write your blog post content here... or upload a Word document using the button above."
                style={{ whiteSpace: 'pre-wrap' }}
              />
            </div>
            <p className="text-foreground-400 font-body text-[10px] mt-1">
              Use the toolbar above to format text. Upload a .docx file to import content automatically. Supports headings, bold, italic, lists, links, images, and more.
            </p>
          </div>

          {/* SEO */}
          <div className="border-t border-background-200/70 pt-5 mb-5">
            <h3 className="font-heading text-foreground-900 text-base mb-3">SEO Settings</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-foreground-500 font-body text-xs mb-1 block">SEO Title</label>
                <input
                  type="text"
                  value={post.seo_title}
                  onChange={(e) => updateField('seo_title', e.target.value)}
                  className="w-full border border-background-300 rounded-md px-3 py-1.5 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-background-50"
                  placeholder="Leave empty to use post title"
                />
              </div>
              <div>
                <label className="text-foreground-500 font-body text-xs mb-1 block">SEO Description</label>
                <textarea
                  value={post.seo_description}
                  onChange={(e) => updateField('seo_description', e.target.value)}
                  rows={2}
                  className="w-full border border-background-300 rounded-md px-3 py-2 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-background-50 resize-none"
                  placeholder="Meta description for search engines..."
                />
              </div>
            </div>
          </div>

          {/* Action buttons - bottom */}
          <div className="flex items-center justify-between pt-4 border-t border-background-200/70">
            <button
              onClick={() => handleNavClick('/admin')}
              className="px-4 py-2 rounded-md text-sm font-body text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-1" />Return to Blog Posts
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => savePost('draft')}
                disabled={saving}
                className="px-4 py-2 rounded-md text-sm font-body font-medium text-foreground-600 border border-background-300 hover:bg-background-100 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                onClick={handlePreview}
                disabled={saving || !post.title}
                className="px-4 py-2 rounded-md text-sm font-body font-medium text-foreground-600 border border-background-300 hover:bg-background-100 transition-colors whitespace-nowrap disabled:opacity-50 flex items-center gap-1.5"
              >
                <i className="ri-eye-line" />Preview
              </button>
              <button
                onClick={() => savePost('published')}
                disabled={saving || !post.title}
                className="px-4 py-2 rounded-md text-sm font-body font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-50 flex items-center gap-1.5"
              >
                <i className="ri-upload-line" />{post.status === 'published' ? 'Update' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Leave warning modal */}
      {showLeaveWarning && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-foreground-950/50 backdrop-blur-sm">
          <div className="bg-background-50 rounded-lg p-6 max-w-[440px] w-full mx-4">
            <h3 className="font-heading text-foreground-900 text-lg mb-2">You have unsaved changes</h3>
            <p className="text-foreground-600 font-body text-sm mb-6">
              Would you like to save them before leaving?
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={async () => {
                  setShowLeaveWarning(false);
                  await savePost('draft');
                  if (pendingNavigation) navigate(pendingNavigation);
                }}
                className="w-full px-4 py-2.5 rounded-md text-sm font-body font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Save and Leave
              </button>
              <button
                onClick={() => {
                  setShowLeaveWarning(false);
                  setUnsavedChanges(false);
                  if (pendingNavigation) navigate(pendingNavigation);
                }}
                className="w-full px-4 py-2.5 rounded-md text-sm font-body text-foreground-600 border border-background-300 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Leave Without Saving
              </button>
              <button
                onClick={() => { setShowLeaveWarning(false); setPendingNavigation(null); }}
                className="w-full px-4 py-2.5 rounded-md text-sm font-body text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Stay on This Page
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}